import React, { useMemo } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { safeDateTimeFormat } from '@localization/safeIntl';

type RestrictedStepProps = Readonly<{
  nextWithdrawalAvailableAtMs: number;
  onDone: () => void;
}>;

export const RestrictedStep: React.FC<RestrictedStepProps> = ({ nextWithdrawalAvailableAtMs, onDone }) => {
  const { tq } = useTranslation('cashFundsAdmin');

  const formattedTime = useMemo(() => {
    const date = new Date(nextWithdrawalAvailableAtMs);
    const parts = safeDateTimeFormat(navigator.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).formatToParts(date);

    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
    const datePart = `${get('weekday')}, ${get('month')} ${get('day')}`;
    const dayPeriod = get('dayPeriod');
    const timePart = `${get('hour')}:${get('minute')}${dayPeriod ? ` ${dayPeriod}` : ''} ${get('timeZoneName')}`;
    return `${datePart} · ${timePart}`;
  }, [nextWithdrawalAvailableAtMs]);

  return (
    <Flex
      flexDirection="column"
      flex={1}
    >
      <TextV2
        textAlign="center"
        fontSize={pxToRem(24)}
        fontWeight={600}
        color="mono14"
        letterSpacing="-0.024em"
        lineHeight="1.3"
        marginBottom={6}
      >
        {tq`Transfer not available yet`}
      </TextV2>

      <Flex
        flexDirection="column"
        flex={1}
        justifyContent="center"
      >
        <TextV2
          textAlign="center"
          fontSize={pxToRem(15)}
          color="mono10"
          lineHeight="1.55"
          marginBottom={7}
        >
          {tq`For your account's security, there's a short waiting period between transfers. You can send your next one at the time below.`}
        </TextV2>

        <Box
          backgroundColor="mono2"
          borderRadius={3}
          padding={6}
          textAlign="center"
        >
          <TextV2
            display="block"
            fontSize={pxToRem(20)}
            color="mono10"
            letterSpacing="0.01em"
            marginBottom={2}
          >
            {tq`Next transfer available`}
          </TextV2>
          <TextV2
            display="block"
            fontSize={pxToRem(22)}
            fontWeight={700}
            color="mono14"
            letterSpacing="-0.01em"
          >
            {formattedTime}
          </TextV2>
        </Box>
      </Flex>

      <ButtonV2
        onClick={onDone}
        size="lg"
        intent="neutral"
        shape="rounded"
        fullWidth
        marginTop={6}
      >
        {tq`Got it`}
      </ButtonV2>
    </Flex>
  );
};
